/**
 * Fase B / B3 — planilha de INVENTÁRIO do Trade-in (DEMO-03, prototipo/src/planilha.ts):
 * modelo com identificação na linha 1 e cabeçalho na linha 2; prévia com linhas
 * válidas, inválidas, duplicadas e ignoradas; nada entra no inventário antes da
 * revisão. Leitura pelo leitor de .xlsx com limites de descompactação (planilha.ts).
 */
import { gerarXlsx } from './planilha.ts';
import type { ComponenteOperacao } from './propostas.ts';

export const VERSAO_MODELO_INVENTARIO = 'INVENTARIO-TRADEIN v2';
/** Modelo baixado na DEMO-03 (mesmas colunas): continua aceito. */
const VERSOES_ACEITAS = [VERSAO_MODELO_INVENTARIO, 'INVENTARIO-TRADEIN v2 (DEMO-03)'];
export const COLUNAS_INVENTARIO = ['Categoria', 'Fabricante', 'Modelo', 'Part number', 'Serial number', 'Processador', 'HD', 'Storage', 'Memória', 'Tela', 'Funcional', 'Acessórios', 'Observações', 'Quantidade'] as const;
export const OBRIGATORIAS = ['Categoria', 'Fabricante', 'Modelo'] as const;
export const LIMITES_INVENTARIO = { bytes: 700 * 1024, linhas: 1000 };
const CAMPO: Record<(typeof COLUNAS_INVENTARIO)[number], keyof ComponenteOperacao> = {
  Categoria: 'categoria',
  Fabricante: 'fabricante',
  Modelo: 'modelo',
  'Part number': 'pn',
  'Serial number': 'serial',
  Processador: 'processador',
  HD: 'hd',
  Storage: 'storage',
  Memória: 'memoria',
  Tela: 'tela',
  Funcional: 'funcional',
  Acessórios: 'acessorios',
  Observações: 'observacoes',
  Quantidade: 'quantidade',
};

/** Modelo oficial: linha 1 = identificação do modelo; linha 2 = cabeçalho; linha 3 = exemplo fictício. */
export const gerarModeloInventario = () =>
  gerarXlsx(
    [
      [VERSAO_MODELO_INVENTARIO, 'Preencha a partir da linha 3. Categoria, Fabricante e Modelo são obrigatórios. Quantidade: inteiro a partir de 1 (vazio = 1). Lote sem serial: use a quantidade.'],
      [...COLUNAS_INVENTARIO],
      ['Monitor', 'Fabricante Exemplo', 'Modelo Exemplo 24', 'PN-EXEMPLO', 'SN-EXEMPLO-0001', '', '', '', '', '24 polegadas', 'Sim', 'Cabo', 'Linha de exemplo: apague antes de importar', 1],
    ],
    'Inventario',
  );

/** Impressão digital do conteúdo (FNV-1a) — impede importar o mesmo arquivo duas vezes. */
export function assinatura(b: Uint8Array) {
  let h = 0x811c9dc5;
  for (let i = 0; i < b.length; i++) h = Math.imul(h ^ b[i], 0x01000193) >>> 0;
  return `${h.toString(16).padStart(8, '0')}-${b.length}`;
}

export type StatusLinha = 'valida' | 'invalida' | 'duplicada' | 'ignorada';
export interface LinhaPrevia {
  numero: number;
  status: StatusLinha;
  motivo: string;
  equipamento?: Partial<ComponenteOperacao>;
}
export interface Previa {
  erroModelo?: string;
  linhas: LinhaPrevia[];
  contagem: Record<StatusLinha, number>;
}
const chaveEquip = (e: Partial<ComponenteOperacao>) => [e.categoria, e.fabricante, e.modelo, e.pn, e.serial].map((x) => String(x ?? '').trim().toLowerCase()).join('|');
const serialIndividual = (s?: string) => Boolean(s && s.trim() && !/lote/i.test(s));

/** Valida a planilha lida: modelo, colunas, erros por linha, duplicidades (no arquivo e com o inventário atual). */
export function validarPlanilhaInventario(linhas: string[][], existentes: ComponenteOperacao[]): Previa {
  const contagem: Record<StatusLinha, number> = { valida: 0, invalida: 0, duplicada: 0, ignorada: 0 };
  const marcador = (linhas[0]?.[0] ?? '').trim();
  if (!VERSOES_ACEITAS.includes(marcador)) {
    const antigo = /INVENTARIO/i.test(marcador) || linhas.some((l) => l.some((c) => /^(descri[cç][aã]o|condi[cç][aã]o|lote)$/i.test(c.trim())));
    return { erroModelo: antigo ? `Modelo antigo ou alterado (${marcador || 'sem identificação'}): baixe o modelo atual (${VERSAO_MODELO_INVENTARIO}) e preencha de novo.` : `Arquivo fora do modelo: a linha 1 deve identificar “${VERSAO_MODELO_INVENTARIO}”. Baixe o modelo.`, linhas: [], contagem };
  }
  if (linhas.length - 2 > LIMITES_INVENTARIO.linhas) return { erroModelo: `A planilha tem mais de ${LIMITES_INVENTARIO.linhas} linhas: divida em arquivos menores.`, linhas: [], contagem };
  const cab = (linhas[1] ?? []).map((c) => c.trim());
  const faltam = COLUNAS_INVENTARIO.filter((c) => !cab.includes(c));
  const sobram = cab.filter((c) => c && !(COLUNAS_INVENTARIO as readonly string[]).includes(c));
  if (faltam.length || sobram.length) return { erroModelo: `Colunas diferentes do modelo.${faltam.length ? ` Faltam: ${faltam.join(', ')}.` : ''}${sobram.length ? ` Não reconhecidas: ${sobram.join(', ')}.` : ''}`, linhas: [], contagem };
  const vistos = new Set(existentes.map(chaveEquip));
  const seriais = new Set(existentes.filter((e) => serialIndividual(e.serial)).map((e) => e.serial!.trim().toLowerCase()));
  const saida: LinhaPrevia[] = [];
  linhas.slice(2).forEach((l, i) => {
    const numero = i + 3;
    const valor = (col: string) => (l[cab.indexOf(col)] ?? '').trim().slice(0, 300);
    if (COLUNAS_INVENTARIO.every((c) => !valor(c))) return saida.push({ numero, status: 'ignorada', motivo: 'linha vazia' });
    if (/linha de exemplo/i.test(valor('Observações'))) return saida.push({ numero, status: 'ignorada', motivo: 'linha de exemplo do modelo' });
    const e: Partial<ComponenteOperacao> = {};
    for (const c of COLUNAS_INVENTARIO) if (c !== 'Quantidade') (e as Record<string, string>)[CAMPO[c]] = valor(c);
    const erros: string[] = OBRIGATORIAS.filter((c) => !valor(c)).map((c) => `${c} obrigatório`);
    const q = valor('Quantidade');
    const qtd = q === '' ? 1 : Number(q.replace(',', '.'));
    if (!Number.isInteger(qtd) || qtd < 1) erros.push(`Quantidade inválida (“${q}”): use um inteiro a partir de 1`);
    if (serialIndividual(e.serial) && qtd !== 1) erros.push('Equipamento com serial individual deve ter quantidade 1 (para lote, deixe o serial vazio ou use “lote”)');
    if (erros.length) return saida.push({ numero, status: 'invalida', motivo: erros.join('; '), equipamento: e });
    e.quantidade = qtd;
    const serial = serialIndividual(e.serial) ? e.serial!.trim().toLowerCase() : '';
    if ((serial && seriais.has(serial)) || vistos.has(chaveEquip(e))) return saida.push({ numero, status: 'duplicada', motivo: serial && seriais.has(serial) ? `serial ${e.serial} já está no inventário ou repetido no arquivo` : 'linha idêntica a um equipamento já existente ou repetida no arquivo', equipamento: e });
    if (serial) seriais.add(serial);
    vistos.add(chaveEquip(e));
    saida.push({ numero, status: 'valida', motivo: serial ? 'equipamento individualizado' : qtd > 1 ? `lote com ${qtd} unidades` : 'unidade sem serial', equipamento: e });
  });
  for (const l of saida) contagem[l.status]++;
  return { linhas: saida, contagem };
}
